import type { QueryClient } from "@tanstack/react-query";
import { storeService } from "@/services/store.service";

/**
 * Tout ce que la position détermine.
 *
 * Changer d'adresse ne change pas un réglage : cela change le catalogue. Les
 * magasins visibles, leur ordre, leurs frais, les carrousels, la bannière, les
 * catégories qui ont du contenu ici — tout est calculé pour un point.
 *
 * La liste était dupliquée et incomplète : l'en-tête rafraîchissait quatre
 * clés, le sélecteur d'adresse sept. Selon l'endroit où le client changeait
 * d'adresse, une partie de l'écran restait donc sur l'ancienne zone.
 *
 * Volontairement absentes : les menus et les prix d'un magasin
 * (`store-menu-*`), qui ne dépendent pas d'où l'on se trouve, et les commandes
 * passées, qui appartiennent au passé.
 */
const LOCATION_DEPENDENT_KEYS = [
  "stores",
  "carousels",
  "carousel-items",
  "banner",
  "app-categories",
  "guest-app-categories",
  "app-category-stores",
  "app-category-stores-infinite",
  "guest-app-category-stores",
  // Frais, distance et disponibilité d'une fiche : tous relatifs au point.
  "store-detail",
  "store-config",
  // Un magasin peut ne pas desservir la nouvelle zone.
  "carts",
  // La liste des favoris porte distance et frais.
  "favorites",
] as const;

/**
 * À appeler après un changement de localisation, une fois la position du compte
 * synchronisée côté serveur.
 *
 * Pour un client connecté, `GET /stores` reflète la nouvelle position
 * immédiatement — vérifié sur l'API — mais le feed de découverte est
 * pré-calculé : sans `invalidateDiscovery`, les carrousels restent sur
 * l'ancienne zone. L'app client fait le même appel, au même moment.
 */
export function refreshForLocationChange(queryClient: QueryClient, authed: boolean): void {
  if (authed) {
    // Au mieux : un échec ne doit pas empêcher le rafraîchissement local.
    storeService.invalidateDiscovery().catch(() => {});
  }
  for (const key of LOCATION_DEPENDENT_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}
